import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Header from "../Header";
import { comboAssignGet, comboAssignSave } from "../../api/comboApi";
import { formatRupees } from "../../utils/providers";
import { ComboChips, errMsg } from "../Combo/comboUtils";
import "../Combo/combo.css";
import "./index.css";

/**
 * Assign combos to one admin / operator and set the price they pay.
 *
 * Route: /combo-assign/:operatorId  (superadmin and admin).
 *  - superadmin sets any price for an admin or operator
 *  - an admin can only hand out combos assigned to them, to their own
 *    operators, and never below their own cost (min_price from the API)
 */
const ComboAssign = () => {
  const { operatorId } = useParams();
  const navigate = useNavigate();

  const [target, setTarget] = useState(null);
  const [rows, setRows] = useState([]); // editable copy
  const [original, setOriginal] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState("");

  const toRows = (combos) =>
    combos.map((c) => ({
      ...c,
      on: !!c.assigned,
      priceInput: c.price !== null && c.price !== undefined ? String(c.price) : "",
      active: !!c.is_active,
    }));

  const snapshot = (list) =>
    JSON.stringify(list.map((r) => [r.combo_id, r.on, r.priceInput, r.active]));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await comboAssignGet(operatorId);
      const list = toRows(data.combos || []);
      setTarget(data.target || null);
      setRows(list);
      setOriginal(snapshot(list));
    } catch (err) {
      toast.error(errMsg(err, "Failed to load combos"));
    } finally {
      setLoading(false);
    }
  }, [operatorId]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = (comboId, change) =>
    setRows((list) => list.map((r) => (r.combo_id === comboId ? { ...r, ...change } : r)));

  const rowError = (r) => {
    if (!r.on) return "";
    if (r.priceInput === "" || Number.isNaN(Number(r.priceInput)) || Number(r.priceInput) < 0) {
      return "Enter a price";
    }
    if (Number(r.priceInput) < Number(r.min_price || 0)) {
      return `Cannot be below your cost of ${formatRupees(r.min_price)}`;
    }
    return "";
  };

  const errors = useMemo(() => rows.filter((r) => rowError(r)).length, [rows]); // eslint-disable-line react-hooks/exhaustive-deps
  const dirty = snapshot(rows) !== original;
  const selected = rows.filter((r) => r.on).length;

  const save = async () => {
    if (errors) {
      toast.warn("Fix the highlighted prices first.");
      return;
    }
    setSaving(true);
    try {
      await comboAssignSave(
        operatorId,
        rows
          .filter((r) => r.on)
          .map((r) => ({ combo_id: r.combo_id, price: Number(r.priceInput), is_active: r.active }))
      );
      toast.success("Combo assignments saved");
      load();
    } catch (err) {
      toast.error(errMsg(err, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  const startPrice = (r) => String(r.min_price || r.base_price || "");
  const setAll = (on) =>
    setRows((list) => list.map((r) => ({ ...r, on, priceInput: on && r.priceInput === "" ? startPrice(r) : r.priceInput })));
  const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
  const shown = rows.filter((r) => !words.length || words.every((w) => `${r.name} ${r.description || ""}`.toLowerCase().includes(w)));

  const roleLabel = target?.role ? String(target.role).charAt(0).toUpperCase() + String(target.role).slice(1) : "";

  return (
    <>
      <Header />
      <div className="cmb-page">
        <div className="cmb-page__head">
          <div>
            <button type="button" className="cmba-back" onClick={() => navigate(-1)}>← Back</button>
            <h1 className="cmb-page__title">
              Assign combos{target?.name ? ` — ${target.name}` : ""}
            </h1>
            <p className="cmb-page__sub">
              {roleLabel ? `${roleLabel} · ` : ""}Tick the combos this account may sell and set the price it is
              charged from its wallet for each recharge.
            </p>
          </div>
          <div className="cmb-actions">
            <button type="button" className="cmb-btn" onClick={load} disabled={loading || saving || !dirty}>
              Reset
            </button>
            <button
              type="button"
              className="cmb-btn cmb-btn--primary"
              onClick={save}
              disabled={loading || saving || !dirty || errors > 0}
            >
              {saving ? "Saving…" : `Save${selected ? ` (${selected})` : ""}`}
            </button>
          </div>
        </div>

        {loading ? (
          <div className="cmb-empty">Loading combos…</div>
        ) : rows.length === 0 ? (
          <div className="cmb-empty">
            <strong>No combos to assign</strong>
            Combos you can hand out will appear here once they are created and assigned to you.
          </div>
        ) : (
          <>
          <div className="cmba-toolbar">
            <input
              type="search"
              className="cmba-search"
              placeholder="Search combos…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              aria-label="Search combos"
            />
            <span className="cmba-count">{selected} of {rows.length} assigned</span>
            <div className="cmb-actions">
              <button type="button" className="cmb-btn cmb-btn--sm" onClick={() => setAll(true)} disabled={selected === rows.length}>Assign all</button>
              <button type="button" className="cmb-btn cmb-btn--sm" onClick={() => setAll(false)} disabled={!selected}>Clear</button>
            </div>
          </div>
          <div className="cmb-panel cmb-table-wrap">
            <table className="cmb-table cmba-table">
              <thead>
                <tr>
                  <th style={{ width: 44 }}>
                    <span className="cmba-sr">Assigned</span>
                  </th>
                  <th>Combo</th>
                  <th className="cmb-num">Your cost</th>
                  <th>Price to charge (₹)</th>
                  <th>Enabled</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 && (
                  <tr><td colSpan={5} className="cmb-muted" style={{ padding: 16 }}>No combo matches “{q}”.</td></tr>
                )}
                {shown.map((r) => {
                  const err = rowError(r);
                  const margin = r.on && r.priceInput !== "" && !err ? Number(r.priceInput) - Number(r.min_price || 0) : null;
                  return (
                    <tr key={r.combo_id} className={r.on ? "cmba-on" : ""}>
                      <td data-label="Assigned">
                        <input
                          type="checkbox"
                          className="cmba-check"
                          checked={r.on}
                          aria-label={`Assign ${r.name}`}
                          onChange={(e) =>
                            patch(r.combo_id, {
                              on: e.target.checked,
                              priceInput:
                                e.target.checked && r.priceInput === ""
                                  ? startPrice(r)
                                  : r.priceInput,
                            })
                          }
                        />
                      </td>
                      <td data-label="Combo">
                        <strong>{r.name}</strong>
                        {r.description ? <div className="cmb-muted">{r.description}</div> : null}
                        <div style={{ marginTop: 6 }}>
                          <ComboChips items={r.items} />
                        </div>
                      </td>
                      <td data-label="Your cost" className="cmb-num">
                        {formatRupees(r.min_price || 0)}
                      </td>
                      <td data-label="Price">
                        <input
                          type="number"
                          min={r.min_price || 0}
                          step="0.01"
                          className={`cmba-price ${err ? "is-bad" : ""}`}
                          value={r.priceInput}
                          disabled={!r.on}
                          aria-label={`Price for ${r.name}`}
                          onChange={(e) => patch(r.combo_id, { priceInput: e.target.value })}
                        />
                        {err ? <div className="cmb-error-text">{err}</div> : null}
                        {margin !== null && Number(r.min_price || 0) > 0 ? (
                          <div className={`cmba-margin ${margin > 0 ? "is-up" : ""}`}>
                            {margin > 0 ? `+${formatRupees(margin)} over your cost` : "Same as your cost"}
                          </div>
                        ) : null}
                      </td>
                      <td data-label="Enabled">
                        <label className="cmba-switch">
                          <input
                            type="checkbox"
                            checked={r.active}
                            disabled={!r.on}
                            onChange={(e) => patch(r.combo_id, { active: e.target.checked })}
                          />
                          <span>{r.active ? "On" : "Off"}</span>
                        </label>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
    </>
  );
};

export default ComboAssign;
